/* ─────────────────────────────────────────────────────────────────────────────
 * Structured Literacy 4-Week Reading Curriculum
 *
 * Follows the structured-literacy sequence: phoneme awareness → phoneme-grapheme mapping →
 * blending/segmenting → decoding in text. Each lesson is multi-sensory:
 * "Say it, Tap it, Write it" (auditory + kinesthetic + visual).
 *
 * Scope & Sequence
 * ─────────────────
 * Week 1 (OG1-01 → OG1-05): Short Vowels — CVC pattern
 * Week 2 (OG2-01 → OG2-05): Consonant Blends & Digraphs
 * Week 3 (OG3-01 → OG3-05): Long Vowels — VCe & Vowel Teams
 * Week 4 (OG4-01 → OG4-05): Multi-Syllabic Decoding (2, 3, 4+ syllables)
 *
 * IDs use prefix OG so they never collide with base lesson IDs (L1-nn, W1-nn).
 * ─────────────────────────────────────────────────────────────────────────── */

import type { SkillLevel } from './skillLevel';
import type { LessonDetail } from './offlineLessons';

// ─── Re-export for convenience ────────────────────────────────────────────────
export type { LessonDetail };

// ─── Week 1: Short Vowels ────────────────────────────────────────────────────

const week1: LessonDetail[] = [
  {
    id: 'OG1-01',
    title: 'Short /a/ Sound — Say It, Tap It, Read It',
    titleFr: 'Son /a/ court — Dis-le, Tape-le, Lis-le',
    level: 1 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 1 — Short Vowel /a/\n\nToday we learn the short /a/ sound. It sounds like the "a" in "cat". \n\nSay it with me: /a/ /a/ /a/ (like you\'re at the doctor and they say "open wide — ahh!")',
        contentFr: 'Leçon sons et lettres 1 — Voyelle courte /a/\n\nAujourd\'hui on apprend le son court /a/. Il ressemble au "a" dans "chat". \n\nDis avec moi : /a/ /a/ /a/',
      },
      {
        type: 'word-tile',
        content: 'a',
        contentFr: 'a',
        audioRef: 'phoneme_short_a',
      },
      {
        type: 'text',
        content: '🖐 TAP IT: Tap one finger for each sound you hear in "cat".\nc - a - t → 3 taps!\n\nTry these words — tap a finger for each sound:',
        contentFr: '🖐 TAPE-LE : Tape un doigt pour chaque son dans "chat".\nc - h - a - t → 4 taps !\n\nEssaie ces mots — tape un doigt pour chaque son :',
      },
      { type: 'word-tile', content: 'cat', contentFr: 'rat', audioRef: 'word_cat' },
      { type: 'word-tile', content: 'hat', contentFr: 'bat', audioRef: 'word_hat' },
      { type: 'word-tile', content: 'map', contentFr: 'cap', audioRef: 'word_map' },
      { type: 'word-tile', content: 'bag', contentFr: 'sac', audioRef: 'word_bag' },
      { type: 'word-tile', content: 'fan', contentFr: 'van', audioRef: 'word_fan' },
      { type: 'word-tile', content: 'sad', contentFr: 'mad', audioRef: 'word_sad' },
      {
        type: 'text',
        content: '✍️ WRITE IT: Now trace these letters in the air with your finger:\nA  →  a\n\nAll of these words have the short /a/ in the MIDDLE. That is the vowel! A vowel is the "heart" of every syllable.',
        contentFr: '✍️ ÉCRIS-LE : Trace ces lettres dans l\'air avec ton doigt :\nA  →  a\n\nTous ces mots ont le /a/ court AU MILIEU. C\'est la voyelle ! Une voyelle est le "cœur" de chaque syllabe.',
      },
      {
        type: 'text',
        content: 'Read these sentences aloud:\n• "The fat cat sat on a mat."\n• "Dan can tap and clap."\n\nNice work! You decoded short /a/ words! 🌟',
        contentFr: 'Lis ces phrases à voix haute :\n• "Le gros chat est assis sur un tapis."\n• "Dan peut taper et applaudir."\n\nBravo ! Tu as décodé des mots avec le /a/ court ! 🌟',
      },
    ],
  },

  {
    id: 'OG1-02',
    title: 'Short /i/ Sound — Sit, Sip, Tip',
    titleFr: 'Son /i/ court — Assis, Boire, Bout',
    level: 1 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 2 — Short Vowel /i/\n\nThe short /i/ says /ĭ/ — like in "it" or "fish". \nPut your finger under your chin. Say /ĭ/ — your chin barely moves! That\'s the short /i/.',
        contentFr: 'Leçon sons et lettres 2 — Voyelle courte /i/\n\nLe /i/ court dit /ĭ/ — comme dans "il" ou "fil".\nMets ton doigt sous ton menton. Dis /ĭ/ — ton menton bouge à peine !',
      },
      { type: 'word-tile', content: 'i', contentFr: 'i', audioRef: 'phoneme_short_i' },
      {
        type: 'text',
        content: '🖐 TAP IT — tap a finger for each sound:\n"sit" → /s/ /ĭ/ /t/ = 3 taps\n"fish" → /f/ /ĭ/ /sh/ = 3 taps (the "sh" is ONE sound!)',
        contentFr: '🖐 TAPE-LE — tape un doigt pour chaque son :\n"lit" → /l/ /ĭ/ /t/ = 3 taps',
      },
      { type: 'word-tile', content: 'sit', contentFr: 'lit', audioRef: 'word_sit' },
      { type: 'word-tile', content: 'pin', contentFr: 'fin', audioRef: 'word_pin' },
      { type: 'word-tile', content: 'bit', contentFr: 'kit', audioRef: 'word_bit' },
      { type: 'word-tile', content: 'win', contentFr: 'vin', audioRef: 'word_win' },
      { type: 'word-tile', content: 'lip', contentFr: 'dip', audioRef: 'word_lip' },
      { type: 'word-tile', content: 'hid', contentFr: 'mid', audioRef: 'word_hid' },
      {
        type: 'text',
        content: '✍️ WRITE IT: Trace an "i" in the air — a tall stick with a dot on top!\n\nNow read this sentence:\n• "The big fish did a flip in the lid."\n\nCan you hear all the short /i/ words? Count them! 🐟',
        contentFr: '✍️ ÉCRIS-LE : Trace un "i" dans l\'air — un bâton avec un point en haut !\n\nLis cette phrase :\n• "Le grand poisson a fait un saut dans le filet."',
      },
    ],
  },

  {
    id: 'OG1-03',
    title: 'Short /o/ Sound — Hot Dog, Top Dog',
    titleFr: 'Son /o/ court — Chaud, Pot, Rot',
    level: 1 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 3 — Short Vowel /o/\n\nThe short /o/ says /ŏ/ — like "o" in "hot" or "stop".\nOpen your mouth wide like you\'re saying "awe" but shorter: /ŏ/!',
        contentFr: 'Leçon sons et lettres 3 — Voyelle courte /o/\n\nLe /o/ court dit /ŏ/ — comme dans "sol" ou "pot".\nOuvre la bouche comme pour dire "oh" mais plus court : /ŏ/ !',
      },
      { type: 'word-tile', content: 'o', contentFr: 'o', audioRef: 'phoneme_short_o' },
      {
        type: 'text',
        content: '🖐 TAP it — count the sounds:\n"hot" → /h/ /ŏ/ /t/ = 3 taps\n"stop" → /s/ /t/ /ŏ/ /p/ = 4 taps  ← a BLEND at the start!',
        contentFr: '🖐 TAPE-LE :\n"pot" → /p/ /ŏ/ /t/ = 3 taps',
      },
      { type: 'word-tile', content: 'hot', contentFr: 'pot', audioRef: 'word_hot' },
      { type: 'word-tile', content: 'top', contentFr: 'cop', audioRef: 'word_top' },
      { type: 'word-tile', content: 'dot', contentFr: 'rot', audioRef: 'word_dot' },
      { type: 'word-tile', content: 'sob', contentFr: 'mob', audioRef: 'word_sob' },
      { type: 'word-tile', content: 'fog', contentFr: 'log', audioRef: 'word_fog' },
      { type: 'word-tile', content: 'hop', contentFr: 'mop', audioRef: 'word_hop' },
      {
        type: 'text',
        content: '✍️ WRITE IT: Draw a circle for "o" — nice and round!\n\nRead aloud:\n• "The dog on top got hot."\n• "Bob will hop and not stop."\n\nExcellent short /o/ reading! 🔥',
        contentFr: '✍️ ÉCRIS-LE : Dessine un cercle pour "o" — bien rond !\n\nLis à voix haute :\n• "Le chien en haut a chaud."\n\nExcellente lecture du /o/ court ! 🔥',
      },
    ],
  },

  {
    id: 'OG1-04',
    title: 'Short /e/ and /u/ Sounds',
    titleFr: 'Sons /e/ et /u/ courts',
    level: 1 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 4 — Short Vowels /e/ and /u/\n\nShort /e/ — like "e" in "bed" — your mouth opens a little and your lips spread.\nShort /u/ — like "u" in "cup" — your mouth is relaxed, lips neutral.\n\nListen for the difference:\n• /ĕ/ bed, red, ten\n• /ŭ/ cup, bus, mud',
        contentFr: 'Leçon sons et lettres 4 — Voyelles courtes /e/ et /u/\n\nCourt /e/ — comme dans "bec" — ta bouche s\'ouvre légèrement.\nCourt /u/ — comme dans "bus" — ta bouche est détendue.',
      },
      { type: 'word-tile', content: 'e', contentFr: 'e', audioRef: 'phoneme_short_e' },
      { type: 'word-tile', content: 'u', contentFr: 'u', audioRef: 'phoneme_short_u' },
      {
        type: 'text',
        content: '🖐 TAP short /e/ words:',
        contentFr: '🖐 TAPE les mots avec /e/ court :',
      },
      { type: 'word-tile', content: 'bed', contentFr: 'sec', audioRef: 'word_bed' },
      { type: 'word-tile', content: 'pet', contentFr: 'net', audioRef: 'word_pet' },
      { type: 'word-tile', content: 'red', contentFr: 'gel', audioRef: 'word_red' },
      { type: 'word-tile', content: 'ten', contentFr: 'fen', audioRef: 'word_ten' },
      {
        type: 'text',
        content: '🖐 TAP short /u/ words:',
        contentFr: '🖐 TAPE les mots avec /u/ court :',
      },
      { type: 'word-tile', content: 'cup', contentFr: 'bus', audioRef: 'word_cup' },
      { type: 'word-tile', content: 'mud', contentFr: 'pur', audioRef: 'word_mud' },
      { type: 'word-tile', content: 'bud', contentFr: 'dur', audioRef: 'word_bud' },
      { type: 'word-tile', content: 'run', contentFr: 'nun', audioRef: 'word_run' },
      {
        type: 'text',
        content: '✍️ WRITE IT: Practice writing in the air:\n• e — two arms, one sticking out!\n• u — like a cup that holds water!\n\nRead aloud:\n• "I slept in my red bed."\n• "The pup ran in the mud."\n\nAmazing! You know ALL FIVE short vowels: a, e, i, o, u! ⭐⭐⭐⭐⭐',
        contentFr: '✍️ ÉCRIS-LE :\n• e — comme deux bras !\n• u — comme une tasse !\n\nLis à voix haute :\n• "J\'ai dormi dans mon lit rouge."\n\nIncroyable ! Tu connais les 5 voyelles courtes ! ⭐⭐⭐⭐⭐',
      },
    ],
  },

  {
    id: 'OG1-05',
    title: 'Short Vowel Review — Tap, Sort, Read',
    titleFr: 'Révision voyelles courtes — Taper, Trier, Lire',
    level: 1 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 5 — Short Vowel Review\n\nYou have learned all 5 short vowels:\n/ă/ = a (cat, map)\n/ĕ/ = e (bed, red)\n/ĭ/ = i (sit, pin)\n/ŏ/ = o (hot, dog)\n/ŭ/ = u (cup, mud)\n\nNow we will SORT words by their vowel sound!',
        contentFr: 'Leçon sons et lettres 5 — Révision des voyelles courtes\n\nTu as appris les 5 voyelles courtes :\n/ă/ = a (rat, cap)\n/ĕ/ = e (bec, gel)\n/ĭ/ = i (lit, fin)\n/ŏ/ = o (pot, rot)\n/ŭ/ = u (bus, dur)',
      },
      {
        type: 'text',
        content: '🖐 TAP & IDENTIFY — say each word, tap the sounds, name the vowel:\n\n• "wig" — /w/ /ĭ/ /g/ → SHORT I\n• "fog" — /f/ /ŏ/ /g/ → SHORT O\n• "hen" — /h/ /ĕ/ /n/ → SHORT E\n• "jug" — /j/ /ŭ/ /g/ → SHORT U\n• "nap" — /n/ /ă/ /p/ → SHORT A',
        contentFr: '🖐 TAPE & IDENTIFIE — dis le mot, tape les sons, nomme la voyelle.',
      },
      { type: 'word-tile', content: 'wig', contentFr: 'fig', audioRef: 'word_wig' },
      { type: 'word-tile', content: 'fog', contentFr: 'log', audioRef: 'word_fog_2' },
      { type: 'word-tile', content: 'hen', contentFr: 'ten', audioRef: 'word_hen' },
      { type: 'word-tile', content: 'jug', contentFr: 'lug', audioRef: 'word_jug' },
      { type: 'word-tile', content: 'nap', contentFr: 'cap', audioRef: 'word_nap' },
      { type: 'word-tile', content: 'tip', contentFr: 'dip', audioRef: 'word_tip' },
      { type: 'word-tile', content: 'box', contentFr: 'fox', audioRef: 'word_box' },
      {
        type: 'text',
        content: '📖 READ this decodable passage (all short vowels):\n\n"The big red hen sat on a rock. A fat cat ran up to the hen. "Get back!" said the hen. The cat hid in the mud. The hen got back on her nest and sat."\n\nHow many short vowel words can you find? Count them — there are over 15! 🏆',
        contentFr: '📖 LIS ce texte décodable :\n\n"La grande poule rouge était assise sur un rocher. Un gros chat s\'approcha. \'Recule !\' dit la poule. Le chat se cacha dans la boue. La poule reprit sa place sur son nid."\n\nCombien de mots avec des voyelles courtes peux-tu trouver ? Il y en a plus de 15 ! 🏆',
      },
    ],
  },
];

// ─── Week 2: Consonant Blends & Digraphs ────────────────────────────────────

const week2: LessonDetail[] = [
  {
    id: 'OG2-01',
    title: 'L-Blends: bl, cl, fl, gl, pl, sl',
    titleFr: 'Groupes avec L : bl, cl, fl, gl, pl, sl',
    level: 2 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 6 — Consonant Blends: L-Blends\n\nA BLEND is two consonants that BOTH say their sounds — quickly together!\n\nbl = /b/ + /l/ (both sounds, fast!)\n• blue, black, blink, blend\n\ncl = /c/ + /l/\n• clap, clip, club, class\n\nfl = /f/ + /l/\n• flag, flip, flat, flock\n\ngl = /g/ + /l/\n• glad, glob, glue, glow\n\npl = /p/ + /l/\n• plan, plug, play, plus\n\nsl = /s/ + /l/\n• slip, slim, sled, slug',
        contentFr: 'Leçon sons et lettres 6 — Groupes de consonnes : Groupes avec L\n\nUn GROUPE, c\'est deux consonnes qui disent TOUS LES DEUX leurs sons — vite ensemble !\n\nbl, cl, fl, gl, pl, sl',
      },
      {
        type: 'text',
        content: '🖐 TAP IT — tap EACH sound (blends = 2 taps for 2 sounds):\n"flag" → /f/ /l/ /ă/ /g/ = 4 taps\n"clip" → /k/ /l/ /ĭ/ /p/ = 4 taps',
        contentFr: '🖐 TAPE-LE :\n"bleu" → /b/ /l/ /e/ /u/ = 4 taps',
      },
      { type: 'word-tile', content: 'blue', contentFr: 'bleu', audioRef: 'word_blue' },
      { type: 'word-tile', content: 'clap', contentFr: 'clap', audioRef: 'word_clap' },
      { type: 'word-tile', content: 'flag', contentFr: 'flan', audioRef: 'word_flag' },
      { type: 'word-tile', content: 'glad', contentFr: 'glas', audioRef: 'word_glad' },
      { type: 'word-tile', content: 'play', contentFr: 'plan', audioRef: 'word_play' },
      { type: 'word-tile', content: 'slip', contentFr: 'slim', audioRef: 'word_slip' },
      {
        type: 'text',
        content: '✍️ WRITE IT: Say "blend" slowly as you write each letter in the air.\n\nRead aloud:\n• "The black pup can flip and flop."\n• "Clip the flag to the slim sled."\n• "Glad I can clap — clap, clap, clap!"\n\nYou\'re blending like a pro! 🌟',
        contentFr: '✍️ ÉCRIS-LE : Dis "bleu" lentement en écrivant chaque lettre dans l\'air.\n\nLis à voix haute :\n• "Le chien noir peut glisser et rouler."\n\nTu es un pro des groupes ! 🌟',
      },
    ],
  },

  {
    id: 'OG2-02',
    title: 'R-Blends: br, cr, dr, fr, gr, pr, tr',
    titleFr: 'Groupes avec R : br, cr, dr, fr, gr, pr, tr',
    level: 2 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 7 — Consonant Blends: R-Blends\n\nbr = brag, brim, brick, brush\ncr = crab, crop, crisp, crack\ndr = drip, drum, drag, dress\nfr = frog, from, fret, fresh\ngr = grip, grab, grin, grill\npr = pram, prop, print, press\ntr = trip, trap, trim, truck\n\n🎵 Remember: in a blend, you hear BOTH consonant sounds!',
        contentFr: 'Leçon sons et lettres 7 — Groupes avec R\n\nbr, cr, dr, fr, gr, pr, tr\n\n🎵 Rappel : dans un groupe, tu entends LES DEUX sons de consonnes !',
      },
      {
        type: 'text',
        content: '🖐 TAP IT:\n"frog" → /f/ /r/ /ŏ/ /g/ = 4 taps\n"drum" → /d/ /r/ /ŭ/ /m/ = 4 taps\n"trip" → /t/ /r/ /ĭ/ /p/ = 4 taps',
        contentFr: '🖐 TAPE-LE :\n"gris" → /g/ /r/ /i/ = 3 taps',
      },
      { type: 'word-tile', content: 'brag', contentFr: 'bras', audioRef: 'word_brag' },
      { type: 'word-tile', content: 'crab', contentFr: 'cric', audioRef: 'word_crab' },
      { type: 'word-tile', content: 'drip', contentFr: 'drap', audioRef: 'word_drip' },
      { type: 'word-tile', content: 'frog', contentFr: 'fret', audioRef: 'word_frog' },
      { type: 'word-tile', content: 'grip', contentFr: 'gris', audioRef: 'word_grip' },
      { type: 'word-tile', content: 'pram', contentFr: 'prix', audioRef: 'word_pram' },
      { type: 'word-tile', content: 'trip', contentFr: 'tres', audioRef: 'word_trip' },
      {
        type: 'text',
        content: '📖 READ:\n• "The frog can grip a branch and grin."\n• "A crab grabbed the drum from the tram."\n• "Press and trim the fresh bread."\n\nCount the R-blends you spot! 🐸',
        contentFr: '📖 LIS :\n• "La grenouille peut saisir une branche."\n• "Un crabe a pris le tambour."\n\nCompte les groupes avec R que tu vois ! 🐸',
      },
    ],
  },

  {
    id: 'OG2-03',
    title: 'Final Blends: -st, -nd, -lt, -mp, -nk',
    titleFr: 'Groupes finaux : -st, -nd, -lt, -mp, -nk',
    level: 2 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 8 — Final Consonant Blends\n\nBlends can come at the END of a word too!\n\n-st: best, rest, last, fast, list, fist\n-nd: hand, band, bend, land, kind, find\n-lt: melt, felt, bolt, belt, salt, colt\n-mp: camp, damp, lamp, bump, jump, stomp\n-nk: bank, rank, sink, link, honk, bunk\n\nWhen you see a final blend, say BOTH sounds before you stop.',
        contentFr: 'Leçon sons et lettres 8 — Groupes de consonnes finaux\n\nLes groupes peuvent aussi être à la FIN d\'un mot !\n\n-st : veste, reste\n-nd : grand, fond\n-lt : belt, melt\n-mp : camp, lamp\n-nk : banque, rang',
      },
      {
        type: 'text',
        content: '🖐 TAP IT:\n"best" → /b/ /ĕ/ /s/ /t/ = 4 taps\n"jump" → /j/ /ŭ/ /m/ /p/ = 4 taps\n"sink" → /s/ /ĭ/ /n/ /k/ = 4 taps',
        contentFr: '🖐 TAPE-LE :\n"camp" → /k/ /ă/ /m/ /p/ = 4 taps',
      },
      { type: 'word-tile', content: 'best', contentFr: 'vest', audioRef: 'word_best' },
      { type: 'word-tile', content: 'hand', contentFr: 'band', audioRef: 'word_hand' },
      { type: 'word-tile', content: 'melt', contentFr: 'felt', audioRef: 'word_melt' },
      { type: 'word-tile', content: 'camp', contentFr: 'damp', audioRef: 'word_camp' },
      { type: 'word-tile', content: 'sink', contentFr: 'link', audioRef: 'word_sink' },
      { type: 'word-tile', content: 'jump', contentFr: 'bump', audioRef: 'word_jump' },
      {
        type: 'text',
        content: '📖 READ:\n• "The best camp is by the bank of a pond."\n• "I felt the belt melt in the lamp\'s heat."\n• "Jump and stomp — land with a big BUMP!"\n\nYou can blend at the start AND end! 🦘',
        contentFr: '📖 LIS :\n• "Le meilleur camp est près de la rivière."\n• "Saute et tape du pied — atterris avec un grand BOUM !"\n\nTu sais faire des groupes au début ET à la fin ! 🦘',
      },
    ],
  },

  {
    id: 'OG2-04',
    title: 'Digraphs: sh, ch, th, wh, ph',
    titleFr: 'Digrammes : sh, ch, th, wh, ph',
    level: 2 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 9 — Digraphs\n\nA DIGRAPH is TWO letters that make ONE new sound together:\n\nsh = /sh/ → shop, ship, fish, wish, shut\n   (different from /s/ and /h/ on their own!)\n\nch = /ch/ → chip, much, check, rich, chin\n\nth = /th/ → thin, three, with, math, bath\n   (also voiced: the, that, this, there)\n\nwh = /w/ → when, which, where, why, what\n\nph = /f/ → phone, photo, graph, dolphin',
        contentFr: 'Leçon sons et lettres 9 — Digrammes\n\nUn DIGRAMME, c\'est DEUX lettres qui font UN SEUL son nouveau :\n\nch = /ch/ → chat, bouche\nou = /ou/ → loupe, tour',
      },
      {
        type: 'text',
        content: '🖐 TAP IT — count SOUNDS not letters:\n"ship" → /sh/ /ĭ/ /p/ = 3 sounds (even though "sh" is 2 letters!)\n"chin" → /ch/ /ĭ/ /n/ = 3 sounds\n"thin" → /th/ /ĭ/ /n/ = 3 sounds',
        contentFr: '🖐 TAPE-LE — compte les SONS, pas les lettres :\n"chat" → /ch/ /a/ = 2 sons (même si "ch" c\'est 2 lettres !)',
      },
      { type: 'word-tile', content: 'shop', contentFr: 'chat', audioRef: 'word_shop' },
      { type: 'word-tile', content: 'chip', contentFr: 'chic', audioRef: 'word_chip' },
      { type: 'word-tile', content: 'thin', contentFr: 'then', audioRef: 'word_thin' },
      { type: 'word-tile', content: 'when', contentFr: 'whip', audioRef: 'word_when' },
      { type: 'word-tile', content: 'fish', contentFr: 'wish', audioRef: 'word_fish' },
      { type: 'word-tile', content: 'math', contentFr: 'bath', audioRef: 'word_math' },
      {
        type: 'text',
        content: '✍️ WRITE IT: Write "sh ch th wh" in the air — two letters, ONE sound!\n\n📖 READ:\n• "When did the ship get to the shop?"\n• "The thin fish wished for a dish of chips."\n• "Check the photo — what a big dolphin!"\n\nDigraph superstar! 🦈',
        contentFr: '✍️ ÉCRIS-LE : Écris "ch ou th" dans l\'air — deux lettres, UN son !\n\n📖 LIS :\n• "Quand le bateau est-il arrivé au magasin ?"\n\nChampion des digrammes ! 🦈',
      },
    ],
  },

  {
    id: 'OG2-05',
    title: 'Blends & Digraphs Review — Decode Challenge',
    titleFr: 'Révision groupes & digrammes — Défi de décodage',
    level: 2 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 10 — Blends & Digraphs Review\n\nYou have learned:\n✅ L-blends: bl, cl, fl, gl, pl, sl\n✅ R-blends: br, cr, dr, fr, gr, pr, tr\n✅ Final blends: -st, -nd, -lt, -mp, -nk\n✅ Digraphs: sh, ch, th, wh, ph\n\nNow let\'s DECODE real words — no guessing, just your sound strategy!',
        contentFr: 'Leçon sons et lettres 10 — Révision\n\nTu as appris :\n✅ Groupes avec L : bl, cl, fl, gl, pl, sl\n✅ Groupes avec R : br, cr, dr, fr, gr, pr, tr\n✅ Groupes finaux : -st, -nd, -lt, -mp, -nk\n✅ Digrammes : sh, ch, th, wh, ph',
      },
      {
        type: 'text',
        content: '🖐 TAP & DECODE these longer words:\n• "splash" → /sp/ /l/ /ă/ /sh/ = 4 sounds — has TWO blends + a digraph!\n• "strength" → /str/ /ĕ/ /ng/ /th/ = 4 sounds\n• "shrimp" → /sh/ /r/ /ĭ/ /m/ /p/ = 5 sounds\n• "branch" → /br/ /ă/ /n/ /ch/ = 4 sounds',
        contentFr: '🖐 TAPE & DÉCODE ces mots plus longs.',
      },
      { type: 'word-tile', content: 'splash', contentFr: 'clamp', audioRef: 'word_splash' },
      { type: 'word-tile', content: 'branch', contentFr: 'ranch', audioRef: 'word_branch' },
      { type: 'word-tile', content: 'shrimp', contentFr: 'crimp', audioRef: 'word_shrimp' },
      { type: 'word-tile', content: 'clench', contentFr: 'clench', audioRef: 'word_clench' },
      { type: 'word-tile', content: 'crunch', contentFr: 'bunch', audioRef: 'word_crunch' },
      { type: 'word-tile', content: 'prism', contentFr: 'prison', audioRef: 'word_prism' },
      {
        type: 'text',
        content: '📖 DECODABLE PASSAGE — Blends & Digraphs:\n\n"On a crisp, fresh morning, a shrimp and a crab splashed and swam in the branch of a stream. They went past a clump of plants on the bank. \'This is the best swim!\' said the crab with a grin. The shrimp had to agree."\n\n🏆 Week 2 COMPLETE! You are a Blend & Digraph Champion!',
        contentFr: '📖 PASSAGE DÉCODABLE :\n\n"Par un matin frais, une crevette et un crabe ont nagé dans la rivière. \'C\'est la meilleure nage !\' dit le crabe avec un sourire."\n\n🏆 Semaine 2 TERMINÉE ! Tu es un Champion des groupes et digrammes !',
      },
    ],
  },
];

// ─── Week 3: Long Vowels — VCe & Vowel Teams ───────────────────────────────

const week3: LessonDetail[] = [
  {
    id: 'OG3-01',
    title: 'Long /ā/ — VCe and Vowel Teams (ai, ay)',
    titleFr: 'Long /â/ — Modèle VCe et équipes de voyelles (ai, ay)',
    level: 3 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 11 — Long Vowel /ā/\n\nThe MAGIC E RULE (VCe pattern):\nWhen a word has a vowel, a consonant, and then an "e" at the end — the first vowel says its LONG name and the "e" is silent!\n\na_e → long /ā/:\n• c-a-k-e → /k/ /ā/ /k/ (e is silent!)\n• t-a-p-e → "tape"\n• n-a-m-e → "name"\n• l-a-k-e → "lake"\n\nVowel Teams (two letters, one long sound):\nai → "rain, sail, tail, wait, main"\nay → "day, play, stay, tray, clay"',
        contentFr: 'Leçon sons et lettres 11 — Voyelle longue /ā/\n\nLa RÈGLE du E MAGIQUE (modèle VCe) :\nQuand un mot a une voyelle, une consonne, puis un "e" muet à la fin — la première voyelle dit son NOM LONG !\n\na_e → long /ā/ :\n• g-â-t-e-a-u\n\nÉquipes de voyelles : ai, ay',
      },
      {
        type: 'text',
        content: '🔍 COMPARE short /a/ vs long /ā/:\n• mat → mate (add silent e, vowel says its name!)\n• cap → cape\n• tap → tape\n• plan → plane\n• hat → hate',
        contentFr: '🔍 COMPARE /a/ court vs /ā/ long :\n• mat → mate\n• cap → cape',
      },
      { type: 'word-tile', content: 'cake', contentFr: 'lake', audioRef: 'word_cake' },
      { type: 'word-tile', content: 'name', contentFr: 'fame', audioRef: 'word_name' },
      { type: 'word-tile', content: 'tape', contentFr: 'cape', audioRef: 'word_tape' },
      { type: 'word-tile', content: 'rain', contentFr: 'main', audioRef: 'word_rain' },
      { type: 'word-tile', content: 'play', contentFr: 'clay', audioRef: 'word_play' },
      { type: 'word-tile', content: 'stay', contentFr: 'tray', audioRef: 'word_stay' },
      {
        type: 'text',
        content: '🖐 TAP IT:\n"cake" → /k/ /ā/ /k/ = 3 sounds (e is silent — no tap for it!)\n"rain" → /r/ /ā/ /n/ = 3 sounds (ai is ONE vowel team sound!)\n\n📖 READ:\n• "The name on the cake was Jake."\n• "Stay and play in the rain with Clay."\n\nThe magic e makes your vowel say its name! 🪄',
        contentFr: '🖐 TAPE-LE :\n"gâteau" → 3 sons\n\n📖 LIS :\n• "Le nom sur le gâteau était Jonas."\n\nLe e magique fait dire son nom à la voyelle ! 🪄',
      },
    ],
  },

  {
    id: 'OG3-02',
    title: 'Long /ī/ — VCe (i_e) and Vowel Teams (ie, igh, y)',
    titleFr: 'Long /î/ — VCe et équipes (ie, igh, y)',
    level: 3 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 12 — Long Vowel /ī/\n\ni_e pattern (Magic E):\n• kite, mine, bike, time, pine, wide\n• bite, hide, ride, side, tide\n\nVowel Teams for long /ī/:\nie → pie, tie, lie, die\nigh → high, night, light, right, tight\ny (end of word) → fly, try, sky, cry, dry, spy',
        contentFr: 'Leçon sons et lettres 12 — Voyelle longue /ī/\n\nModèle i_e :\n• kite, mine, bike\n\nÉquipes de voyelles pour /ī/ long :\nie → pie, tie\nigh → night, light\ny → fly, sky',
      },
      {
        type: 'text',
        content: '🔍 COMPARE short /i/ vs long /ī/:\n• bit → bite\n• hid → hide\n• dim → dime\n• fin → fine\n• pin → pine',
        contentFr: '🔍 COMPARE /i/ court vs /ī/ long :\n• bit → bite\n• fin → fine',
      },
      { type: 'word-tile', content: 'kite', contentFr: 'bite', audioRef: 'word_kite' },
      { type: 'word-tile', content: 'mine', contentFr: 'pine', audioRef: 'word_mine' },
      { type: 'word-tile', content: 'bike', contentFr: 'pike', audioRef: 'word_bike' },
      { type: 'word-tile', content: 'night', contentFr: 'light', audioRef: 'word_night' },
      { type: 'word-tile', content: 'fly', contentFr: 'sky', audioRef: 'word_fly' },
      { type: 'word-tile', content: 'pie', contentFr: 'tie', audioRef: 'word_pie' },
      {
        type: 'text',
        content: '🖐 TAP IT:\n"kite" → /k/ /ī/ /t/ = 3 sounds\n"night" → /n/ /ī/ /t/ = 3 sounds (igh = one sound!)\n"fly" → /f/ /l/ /ī/ = 3 sounds (y = long i here!)\n\n📖 READ:\n• "The kite can fly high in the night sky."\n• "I might find my bike by the pine tree."\n\nLong /ī/ makes you smile! 😊',
        contentFr: '📖 LIS :\n• "Le cerf-volant peut voler haut dans le ciel de nuit."\n\nLe /ī/ long te fait sourire ! 😊',
      },
    ],
  },

  {
    id: 'OG3-03',
    title: 'Long /ō/ — VCe (o_e) and Vowel Teams (oa, ow)',
    titleFr: 'Long /ô/ — VCe et équipes (oa, ow)',
    level: 3 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 13 — Long Vowel /ō/\n\no_e pattern (Magic E):\n• home, note, rope, bone, tone, vote\n• hole, mole, pole, role, sole\n\nVowel Teams for long /ō/:\noa → boat, coat, road, soap, toad, goat\now → snow, blow, grow, flow, know, row, slow',
        contentFr: 'Leçon sons et lettres 13 — Voyelle longue /ō/\n\nModèle o_e :\n• home, note, rope\n\nÉquipes de voyelles :\noa → boat, coat, road\now → snow, blow, grow',
      },
      {
        type: 'text',
        content: '🔍 COMPARE short /o/ vs long /ō/:\n• hop → hope\n• rod → rode\n• not → note\n• rob → robe\n• glob → globe',
        contentFr: '🔍 COMPARE /o/ court vs /ō/ long :\n• hop → hope\n• not → note',
      },
      { type: 'word-tile', content: 'home', contentFr: 'dome', audioRef: 'word_home' },
      { type: 'word-tile', content: 'note', contentFr: 'tote', audioRef: 'word_note' },
      { type: 'word-tile', content: 'boat', contentFr: 'coat', audioRef: 'word_boat' },
      { type: 'word-tile', content: 'snow', contentFr: 'flow', audioRef: 'word_snow' },
      { type: 'word-tile', content: 'road', contentFr: 'toad', audioRef: 'word_road' },
      { type: 'word-tile', content: 'rope', contentFr: 'mope', audioRef: 'word_rope' },
      {
        type: 'text',
        content: '🖐 TAP IT:\n"boat" → /b/ /ō/ /t/ = 3 sounds (oa = one team!)\n"snow" → /s/ /n/ /ō/ = 3 sounds (ow = one team!)\n"home" → /h/ /ō/ /m/ = 3 sounds (e is silent!)\n\n📖 READ:\n• "The goat on the road rode home in a coat."\n• "I know the boat will float and not sink."\n\nYou\'ve got long /ō/ nailed! ⛵',
        contentFr: '📖 LIS :\n• "La chèvre sur la route rentra chez elle en manteau."\n\nTu maîtrises le long /ō/ ! ⛵',
      },
    ],
  },

  {
    id: 'OG3-04',
    title: 'Long /ē/ and /ū/ — ee, ea, VCe, ew, ue',
    titleFr: 'Long /ē/ et /ū/ — ee, ea, VCe, ew, ue',
    level: 3 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 14 — Long Vowels /ē/ and /ū/\n\nLong /ē/:\nee → bee, tree, feet, see, free, week\nea → eat, sea, leaf, read, dream, beat\n\nLong /ū/:\nu_e (Magic E) → cube, mule, tune, cute, huge, fume\new → new, few, dew, flew, drew, grew\nue → blue, true, clue, glue, argue\n\nBonus: The letters "e" at end of a 2-letter word says long /ē/ too:\n• me, be, we, he, she',
        contentFr: 'Leçon sons et lettres 14 — Voyelles longues /ē/ et /ū/\n\nLong /ē/ : ee → arbre, pied\n         ea → manger, mer\n\nLong /ū/ : u_e → cube, lune\n           eu/ew → bleu, feu',
      },
      { type: 'word-tile', content: 'tree', contentFr: 'free', audioRef: 'word_tree' },
      { type: 'word-tile', content: 'leaf', contentFr: 'sea', audioRef: 'word_leaf' },
      { type: 'word-tile', content: 'dream', contentFr: 'cream', audioRef: 'word_dream' },
      { type: 'word-tile', content: 'cube', contentFr: 'tube', audioRef: 'word_cube' },
      { type: 'word-tile', content: 'blue', contentFr: 'glue', audioRef: 'word_blue_2' },
      { type: 'word-tile', content: 'grew', contentFr: 'flew', audioRef: 'word_grew' },
      {
        type: 'text',
        content: '📖 READ:\n• "The bee can see the green tree by the sea."\n• "I had a huge dream that I flew to the moon."\n• "The cute blue cube is new — can you see it?"\n\n🖐 TAP IT:\n"dream" → /dr/ /ē/ /m/ = 3 sounds (ea = one team!)\n"cube" → /k/ /ū/ /b/ = 3 sounds (e is silent!)\n\nAmazing! Long vowels are singing their names now! 🎵',
        contentFr: '📖 LIS :\n• "L\'abeille peut voir l\'arbre vert près de la mer."\n\nIncroyable ! Les voyelles longues chantent leur nom maintenant ! 🎵',
      },
    ],
  },

  {
    id: 'OG3-05',
    title: 'Long vs Short Vowel Contrast — The "E" Changes Everything',
    titleFr: 'Voyelle longue vs courte — Le "E" change tout',
    level: 3 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 15 — Long vs Short Vowel Mastery\n\nThe silent "e" changes the whole word\'s meaning!\n\nShort → Long pairs:\n• mat → mate        • bit → bite\n• cap → cape        • dim → dime\n• tap → tape        • fin → fine\n• hat → hate        • hid → hide\n• man → mane        • pin → pine\n• hop → hope        • rob → robe\n• not → note        • cod → code\n• cut → cute        • tub → tube\n• cub → cube        • us → use',
        contentFr: 'Leçon sons et lettres 15 — Maîtrise voyelle longue vs courte\n\nLe "e" muet change le sens de tout le mot !\n\nCourt → Long :\n• mat → mate\n• bit → bite\n• hop → hope',
      },
      {
        type: 'text',
        content: '🔍 DECODE these and tell if the vowel is SHORT or LONG:\n• "The man has a cub in a cage."\n• "I will use a tube for this cute art."\n• "The cub will hide in a big cave."\n\n🖐 TAP pairs to hear the difference:\nshort "mat" → /m/ /ă/ /t/ (mouth open short)\nlong "mate" → /m/ /ā/ /t/ (vowel holds out!)',
        contentFr: '🔍 DÉCODE et dis si la voyelle est COURTE ou LONGUE.\n\n🖐 TAPE les paires pour entendre la différence.',
      },
      { type: 'word-tile', content: 'mat', contentFr: 'cap', audioRef: 'word_mat' },
      { type: 'word-tile', content: 'mate', contentFr: 'cape', audioRef: 'word_mate' },
      { type: 'word-tile', content: 'hop', contentFr: 'rod', audioRef: 'word_hop_2' },
      { type: 'word-tile', content: 'hope', contentFr: 'rode', audioRef: 'word_hope' },
      { type: 'word-tile', content: 'cut', contentFr: 'cub', audioRef: 'word_cut' },
      { type: 'word-tile', content: 'cute', contentFr: 'cube', audioRef: 'word_cute' },
      {
        type: 'text',
        content: '📖 MIXED DECODABLE PASSAGE:\n\n"Jake had a cute red kite. He ran past the lake and let it fly in the blue sky. \'Mine is the best!\' he said with a grin. His dog bit into a pipe and made a huge mess. Jake did not mind — it was a fine, fine day."\n\n🏆 Week 3 COMPLETE! You know ALL long and short vowels! Champion status! 🏅',
        contentFr: '📖 PASSAGE MIXTE :\n\n"Jonas avait un beau cerf-volant rouge. Il courut près du lac et le fit voler dans le ciel bleu. \'Le mien est le meilleur !\' dit-il. C\'était une très belle journée."\n\n🏆 Semaine 3 TERMINÉE ! Tu connais TOUTES les voyelles longues et courtes ! 🏅',
      },
    ],
  },
];

// ─── Week 4: Multi-Syllabic Decoding ─────────────────────────────────────────

const week4: LessonDetail[] = [
  {
    id: 'OG4-01',
    title: 'Syllable Types — The 6 Kinds Every Reader Needs',
    titleFr: 'Types de syllabes — Les 6 types essentiels',
    level: 3 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 16 — The 6 Syllable Types\n\nEvery syllable has ONE vowel sound. The type of syllable tells you HOW to pronounce the vowel.\n\n1️⃣ CLOSED syllable — ends in a consonant → SHORT vowel\n   • cat, hot, pin, bed, cup, rab(bit)\n\n2️⃣ OPEN syllable — ends in a vowel → LONG vowel\n   • me, go, hi, mu(sic), ba(by), o(pen)\n\n3️⃣ MAGIC E (VCe) — vowel-consonant-e → LONG vowel\n   • cake, bike, home, tube\n\n4️⃣ VOWEL TEAM — two vowels together → usually LONG\n   • rain, boat, tree, blue\n\n5️⃣ R-CONTROLLED — vowel + r changes the sound\n   • car, bird, her, fur, for, corn\n\n6️⃣ CONSONANT-LE — at end of word, sounds like /ul/\n   • ta(ble), puz(zle), sim(ple), can(dle)',
        contentFr: 'Leçon sons et lettres 16 — Les 6 types de syllabes\n\nChaque syllabe a UN son de voyelle. Le type de syllabe te dit COMMENT prononcer la voyelle.\n\n1️⃣ FERMÉE — finit par une consonne → voyelle COURTE\n2️⃣ OUVERTE — finit par une voyelle → voyelle LONGUE\n3️⃣ VCe (E MUET) → voyelle LONGUE\n4️⃣ ÉQUIPE DE VOYELLES → souvent LONGUE\n5️⃣ AVEC R → son modifié\n6️⃣ CONSONNE + LE → son /ul/ à la fin',
      },
      { type: 'word-tile', content: 'rabbit', contentFr: 'lapin', audioRef: 'word_rabbit' },
      { type: 'word-tile', content: 'music', contentFr: 'musique', audioRef: 'word_music' },
      { type: 'word-tile', content: 'table', contentFr: 'table', audioRef: 'word_table' },
      { type: 'word-tile', content: 'bird', contentFr: 'third', audioRef: 'word_bird' },
      { type: 'word-tile', content: 'open', contentFr: 'over', audioRef: 'word_open' },
      {
        type: 'text',
        content: '🔍 IDENTIFY the syllable type:\n• "me" → OPEN (ends in vowel, long /ē/)\n• "cat" → CLOSED (ends in t, short /ă/)\n• "cake" → VCe (magic e, long /ā/)\n• "bird" → R-controlled (ir says /er/)\n• "table" → ta-ble (open + consonant-le)\n\nKnowing syllable types is like having a MAP for reading! 🗺️',
        contentFr: '🔍 IDENTIFIE le type de syllabe :\n• "moi" → ouverte\n• "chat" → fermée\n• "table" → table (ouverte + consonne-le)\n\nConnaître les types de syllabes, c\'est comme avoir une CARTE pour lire ! 🗺️',
      },
    ],
  },

  {
    id: 'OG4-02',
    title: '2-Syllable Decoding — Split & Conquer',
    titleFr: 'Décodage en 2 syllabes — Divise et conquiers',
    level: 3 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 17 — Decoding 2-Syllable Words\n\nSTRATEGY: When you see a long word — SPLIT IT!\n\nStep 1: Find the vowels (underline them)\nStep 2: Split between consonants (VC|CV rule)\nStep 3: Read each syllable\nStep 4: Blend the syllables together\n\nExample: "rabbit"\n→ Vowels: a, i\n→ Split: rab | bit\n→ Read: /răb/ + /bĭt/\n→ Blend: "rabbit"!\n\nMore examples:\n• bas | ket → "basket"\n• pil | low → "pillow"\n• gar | den → "garden"\n• can | not → "cannot"\n• pic | nic → "picnic"\n• nap | kin → "napkin"',
        contentFr: 'Leçon sons et lettres 17 — Décodage en 2 syllabes\n\nSTRATÉGIE : Quand tu vois un long mot — DIVISE-LE !\n\nÉtape 1 : Trouve les voyelles\nÉtape 2 : Coupe entre les consonnes (règle V|CV)\nÉtape 3 : Lis chaque syllabe\nÉtape 4 : Combine les syllabes\n\nExemple : "lapin"\n→ la | pin\n→ /la/ + /pɛ̃/\n→ "lapin"!',
      },
      {
        type: 'text',
        content: '🖐 TAP IT by SYLLABLE — tap once per syllable:\n"basket" → bas-ket = 2 taps\n"pillow" → pil-low = 2 taps\n"garden" → gar-den = 2 taps',
        contentFr: '🖐 TAPE-LE par SYLLABE :\n"lapin" → la-pin = 2 taps\n"maison" → mai-son = 2 taps',
      },
      { type: 'word-tile', content: 'basket', contentFr: 'lapin', audioRef: 'word_basket' },
      { type: 'word-tile', content: 'pillow', contentFr: 'oreiller', audioRef: 'word_pillow' },
      { type: 'word-tile', content: 'garden', contentFr: 'jardin', audioRef: 'word_garden' },
      { type: 'word-tile', content: 'picnic', contentFr: 'pique-nique', audioRef: 'word_picnic' },
      { type: 'word-tile', content: 'napkin', contentFr: 'serviette', audioRef: 'word_napkin' },
      { type: 'word-tile', content: 'cannot', contentFr: 'ne pas', audioRef: 'word_cannot' },
      {
        type: 'text',
        content: '📖 READ — split each 2-syllable word as you go:\n• "The rabbit hid in the garden by the basket."\n• "Put the napkin on the pillow at the picnic."\n\n✅ Tip for dyslexia readers: Cover the second syllable, read the first, then uncover and blend — one chunk at a time! 🧩',
        contentFr: '📖 LIS — divise chaque mot en 2 syllabes :\n• "Le lapin se cacha dans le jardin près du panier."\n\n✅ Conseil pour les lecteurs dyslexiques : Cache la deuxième syllabe, lis la première, puis découvre et combine — un morceau à la fois ! 🧩',
      },
    ],
  },

  {
    id: 'OG4-03',
    title: '3-Syllable Decoding — Butterfly, Elephant, Umbrella',
    titleFr: 'Décodage en 3 syllabes — Papillon, Éléphant, Parapluie',
    level: 4 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 18 — Decoding 3-Syllable Words\n\nNow we split into THREE chunks!\n\n"butterfly"\n→ but | ter | fly\n→ /bŭt/ + /tər/ + /flī/\n→ "butterfly"! 🦋\n\n"elephant"\n→ el | e | phant\n→ /ĕl/ + /ĭ/ + /fənt/\n→ "elephant"! 🐘\n\n"umbrella"\n→ um | brel | la\n→ /ŭm/ + /brĕl/ + /ə/\n→ "umbrella"! ☂️\n\nMore 3-syllable words to decode:\n• cam | er | a → "camera"\n• cel | e | brate → "celebrate"\n• hap | pi | ness → "happiness"\n• fan | tas | tic → "fantastic"\n• vol | ca | no → "volcano"\n• com | put | er → "computer"',
        contentFr: 'Leçon sons et lettres 18 — Décodage en 3 syllabes\n\n"papillon"\n→ pa | pil | lon\n→ /pa/ + /pij/ + /lɔ̃/\n→ "papillon"! 🦋\n\n"éléphant"\n→ é | lé | phant\n→ /e/ + /le/ + /fɑ̃/\n→ "éléphant"! 🐘\n\n"parapluie"\n→ pa | ra | pluie\n→ /pa/ + /ra/ + /plɥi/\n→ "parapluie"! ☂️',
      },
      {
        type: 'text',
        content: '🖐 TAP IT — 3 syllable taps:\n"butterfly" → but-ter-fly = 3 taps\n"elephant" → el-e-phant = 3 taps\n"umbrella" → um-brel-la = 3 taps\n"computer" → com-put-er = 3 taps',
        contentFr: '🖐 TAPE-LE — 3 taps de syllabes :\n"papillon" → pa-pil-lon = 3 taps\n"éléphant" → é-lé-phant = 3 taps',
      },
      { type: 'word-tile', content: 'butterfly', contentFr: 'papillon', audioRef: 'word_butterfly' },
      { type: 'word-tile', content: 'elephant', contentFr: 'éléphant', audioRef: 'word_elephant' },
      { type: 'word-tile', content: 'umbrella', contentFr: 'parapluie', audioRef: 'word_umbrella' },
      { type: 'word-tile', content: 'computer', contentFr: 'ordinateur', audioRef: 'word_computer' },
      { type: 'word-tile', content: 'fantastic', contentFr: 'fantastique', audioRef: 'word_fantastic' },
      { type: 'word-tile', content: 'volcano', contentFr: 'volcan', audioRef: 'word_volcano' },
      {
        type: 'text',
        content: '📖 READ — decode each 3-syllable word:\n• "A butterfly landed on the elephant\'s umbrella."\n• "The computer showed a fantastic volcano photo."\n• "Happiness is celebrating with friends!"\n\n🧩 SLOW READER TIP: Say each syllable separately first, THEN blend:\n"but" … "ter" … "fly" → "butterfly"\nUse your finger to slide under each syllable! 🦋',
        contentFr: '📖 LIS :\n• "Un papillon s\'est posé sur le parapluie de l\'éléphant."\n\n🧩 CONSEIL : Dis chaque syllabe séparément d\'abord, PUIS combine ! 🦋',
      },
    ],
  },

  {
    id: 'OG4-04',
    title: '4+ Syllable Decoding — Information, Community, Responsible',
    titleFr: 'Décodage 4+ syllabes — Information, Communauté, Responsable',
    level: 5 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 19 — Decoding 4+ Syllable Words\n\nBig words are just SMALL SYLLABLES joined together!\n\n"information" (5 syllables)\n→ in | for | ma | tion | (no, 4: in|for|ma|tion)\n→ /ĭn/ + /fər/ + /mā/ + /shən/\n→ "information"!\n\n"community" (4 syllables)\n→ com | mu | ni | ty\n→ /kə/ + /myū/ + /nĭ/ + /tē/\n→ "community"!\n\n"responsible" (4 syllables)\n→ re | spon | si | ble\n→ /rĭ/ + /spŏn/ + /sĭ/ + /bəl/\n→ "responsible"!\n\n"celebration" (4 syllables)\n→ cel | e | bra | tion\n→ /sĕl/ + /ĭ/ + /brā/ + /shən/\n→ "celebration"!\n\n"multiplication" (5 syllables)\n→ mul | ti | pli | ca | tion\n→ /mŭl/ + /tĭ/ + /plī/ + /kā/ + /shən/\n→ "multiplication"!',
        contentFr: 'Leçon sons et lettres 19 — Décodage de mots à 4+ syllabes\n\nLes grands mots sont juste des PETITES SYLLABES assemblées !\n\n"information" (4 syllabes)\n→ in | for | ma | tion\n\n"communauté" (4 syllabes)\n→ com | mu | nau | té\n\n"responsable" (4 syllabes)\n→ res | pon | sa | ble\n\n"célébration" (4 syllabes)\n→ cé | lé | bra | tion',
      },
      {
        type: 'text',
        content: '🖐 TAP syllables — count them on your fingers:\n"information" → in-for-ma-tion = 4 taps\n"community" → com-mu-ni-ty = 4 taps\n"celebration" → cel-e-bra-tion = 4 taps\n"multiplication" → mul-ti-pli-ca-tion = 5 taps!',
        contentFr: '🖐 TAPE les syllabes :\n"information" → 4 taps\n"communauté" → 4 taps\n"célébration" → 4 taps',
      },
      { type: 'word-tile', content: 'information', contentFr: 'information', audioRef: 'word_information' },
      { type: 'word-tile', content: 'community', contentFr: 'communauté', audioRef: 'word_community' },
      { type: 'word-tile', content: 'responsible', contentFr: 'responsable', audioRef: 'word_responsible' },
      { type: 'word-tile', content: 'celebration', contentFr: 'célébration', audioRef: 'word_celebration' },
      { type: 'word-tile', content: 'imagination', contentFr: 'imagination', audioRef: 'word_imagination' },
      { type: 'word-tile', content: 'impossible', contentFr: 'impossible', audioRef: 'word_impossible' },
      {
        type: 'text',
        content: '📖 READ — take your time with each syllable:\n• "The community shared information about the celebration."\n• "It is impossible to read without imagination!"\n• "Being responsible means you can be trusted."\n\n🎯 DECODING STRATEGY for big words:\n1. Count the syllables (tap them)\n2. Identify the vowel in each syllable\n3. Apply syllable type rules\n4. Blend syllable by syllable\n5. Say the whole word — does it sound right?\n\nYou can read ANY word now! 🚀',
        contentFr: '📖 LIS — prends ton temps avec chaque syllabe :\n• "La communauté a partagé des informations sur la célébration."\n\n🎯 STRATÉGIE pour les grands mots :\n1. Compte les syllabes\n2. Identifie la voyelle dans chaque syllabe\n3. Applique les règles du type de syllabe\n4. Combine syllabe par syllabe\n5. Dis le mot entier — est-ce que ça sonne bien ?\n\nTu peux lire N\'IMPORTE QUEL mot maintenant ! 🚀',
      },
    ],
  },

  {
    id: 'OG4-05',
    title: 'Multi-Syllabic Mastery — Fluency & Dyslexia Strategies',
    titleFr: 'Maîtrise multi-syllabique — Fluidité et stratégies dyslexie',
    level: 4 as SkillLevel,
    contentBlocks: [
      {
        type: 'text',
        content: 'Sound-Spelling Lesson 20 — Multi-Syllabic Mastery\n\n🎓 COMPLETE DECODING TOOLKIT:\n\n✅ Phoneme awareness — tap sounds in every syllable\n✅ Short vowels — a, e, i, o, u in closed syllables\n✅ Long vowels — magic e, vowel teams, open syllables\n✅ Blends — l-blends, r-blends, final blends\n✅ Digraphs — sh, ch, th, wh, ph\n✅ 2-syllable words — VC|CV split strategy\n✅ 3-syllable words — tap and chunk\n✅ 4+ syllable words — syllable-by-syllable decoding\n\n🧠 DYSLEXIA READING STRATEGIES:\n• Use a finger or card to track syllables\n• Read syllable by syllable before blending\n• If you get stuck, cover everything except the first syllable\n• Check: does the word look familiar after decoding?\n• Re-read the sentence if a word seems wrong',
        contentFr: 'Leçon sons et lettres 20 — Maîtrise multi-syllabique\n\n🎓 BOÎTE À OUTILS COMPLÈTE DE DÉCODAGE :\n\n✅ Conscience phonémique — tape les sons dans chaque syllabe\n✅ Voyelles courtes — a, e, i, o, u dans les syllabes fermées\n✅ Voyelles longues — e magique, équipes de voyelles, syllabes ouvertes\n✅ Groupes — groupes avec L, R, finaux\n✅ Digrammes — ch, ou, th\n✅ Mots à 2 syllabes — stratégie de division\n✅ Mots à 3 syllabes — taper et grouper\n✅ Mots à 4+ syllabes — décodage syllabe par syllabe',
      },
      {
        type: 'text',
        content: '📖 FINAL FLUENCY PASSAGE — full month of skills in one text:\n\n"Yesterday, a butterfly and an elephant decided to celebrate. They made an extraordinary invitation and sent it to their community. \'Come celebrate imagination!\' it said. The responsible crab planned the celebration. He got information about the best location.\n\nEveryone clapped and cheered. The butterfly did a flip in the blue sky. The elephant blew his enormous trunk like a trumpet. It was an unforgettable celebration of friendship and learning."\n\n🏆 4-WEEK STRUCTURED LITERACY JOURNEY COMPLETE!\n\nYou have learned:\n📌 All 5 short vowels\n📌 All 5 long vowel patterns\n📌 Consonant blends and digraphs\n📌 Decoding 2, 3, 4, and 5+ syllable words\n\nYou are an incredible reader! 🌟🌟🌟',
        contentFr: '📖 PASSAGE FINAL — toutes les compétences du mois en un texte :\n\n"Hier, un papillon et un éléphant ont décidé de faire la fête. Ils ont fait une invitation extraordinaire et l\'ont envoyée à leur communauté. \'Venez célébrer l\'imagination !\' disait-elle. Le crabe responsable a planifié la célébration.\n\nTout le monde a applaudi. Le papillon a fait un saut dans le ciel bleu. L\'éléphant a soufflé sa trompe comme une trompette. C\'était une célébration inoubliable de l\'amitié et de l\'apprentissage."\n\n🏆 PARCOURS DE 4 SEMAINES EN LITTÉRATIE STRUCTURÉE TERMINÉ !\n\nTu as appris :\n📌 Les 5 voyelles courtes\n📌 Les 5 modèles de voyelles longues\n📌 Les groupes et digrammes de consonnes\n📌 Le décodage de mots à 2, 3, 4 et 5+ syllabes\n\nTu es un lecteur incroyable ! 🌟🌟🌟',
      },
    ],
  },
];

// ─── Assembled curriculum ─────────────────────────────────────────────────────

export const OG_READING_LESSONS: LessonDetail[] = [
  ...week1,
  ...week2,
  ...week3,
  ...week4,
];

export const OG_LESSON_IDS = OG_READING_LESSONS.map(l => l.id);

/** Get structured-literacy lessons for a given level (returns all lessons at or below that level) */
export function getOGLessonsForLevel(level: number): LessonDetail[] {
  return OG_READING_LESSONS.filter(l => l.level <= level);
}

/** Get a single structured-literacy lesson by ID */
export function getOGLessonById(id: string): LessonDetail | undefined {
  return OG_READING_LESSONS.find(l => l.id === id);
}

/** Get all structured-literacy lesson IDs grouped by week */
export const OG_WEEKS = {
  week1: week1.map(l => l.id),   // Short Vowels
  week2: week2.map(l => l.id),   // Blends & Digraphs
  week3: week3.map(l => l.id),   // Long Vowels
  week4: week4.map(l => l.id),   // Multi-Syllabic Decoding
} as const;

/** Metadata for the curriculum overview */
export const OG_CURRICULUM_META = {
  totalLessons: OG_READING_LESSONS.length,
  weeks: 4,
  approach: 'Structured Literacy (Science of Reading aligned)',
  skills: [
    'Phoneme awareness (tapping/segmenting)',
    'Short vowels: a, e, i, o, u (CVC pattern)',
    'Consonant blends: L-blends, R-blends, final blends',
    'Digraphs: sh, ch, th, wh, ph',
    'Long vowels: VCe magic-e pattern',
    'Long vowels: vowel teams (ai, ay, oa, ow, ee, ea, ue, ew)',
    'Long vs short vowel contrast',
    '6 syllable types',
    '2-syllable decoding (VC|CV split)',
    '3-syllable decoding (chunk & blend)',
    '4+ syllable decoding (multi-step)',
    'Fluency with decodable passages',
  ],
  dyslexiaStrategies: [
    'Multi-sensory: Say it, Tap it, Write it (every lesson)',
    'Phoneme tapping with fingers',
    'Syllable-by-syllable decoding',
    'Cover/uncover syllable technique',
    'Finger tracking for fluency',
    'Explicit short vs long vowel contrast',
    'Decodable passages (no guessing required)',
  ],
};
